import React, { useState, useEffect, useRef } from 'react';
import { supportAPI } from '../../services/api';

const CATEGORIES = ['Appetizer', 'Main Course', 'Dessert', 'Beverages', 'Snacks', 'Sides'];
const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

function SupportRestaurantManager() {
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('menu');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [menuItems, setMenuItems] = useState([]);
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuCategory, setMenuCategory] = useState('');
  const [menuSearch, setMenuSearch] = useState('');
  const [showMenuForm, setShowMenuForm] = useState(false);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [menuForm, setMenuForm] = useState({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 });
  const menuImageRef = useRef(null);

  const [coupons, setCoupons] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' });

  const [settings, setSettings] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        setLoading(true);
        const { data } = await supportAPI.getMyRestaurants();
        setRestaurants(data.data?.restaurants || []);
      } catch (err) {
        setMessage({ type: 'error', text: 'Failed to load assigned restaurants' });
      } finally {
        setLoading(false);
      }
    };
    fetchRestaurants();
  }, []);

  const filteredRestaurants = restaurants.filter(r =>
    r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.address?.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectRestaurant = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setActiveTab('menu');
  };

  // Menu functions
  const fetchMenu = async () => {
    if (!selectedRestaurant) return;
    try {
      setMenuLoading(true);
      const params = {};
      if (menuCategory) params.category = menuCategory;
      if (menuSearch) params.search = menuSearch;
      const { data } = await supportAPI.getRestaurantMenu(selectedRestaurant._id, params);
      setMenuItems(data.data?.menuItems || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load menu' });
    } finally {
      setMenuLoading(false);
    }
  };

  useEffect(() => { if (selectedRestaurant && activeTab === 'menu') fetchMenu(); }, [selectedRestaurant, activeTab, menuCategory, menuSearch]);

  const handleMenuSubmit = async () => {
    try {
      const formData = new FormData();
      Object.entries(menuForm).forEach(([k, v]) => formData.append(k, v));
      if (menuImageRef.current?.files[0]) formData.append('image', menuImageRef.current.files[0]);
      if (editingMenuItem) {
        await supportAPI.updateMenuItem(selectedRestaurant._id, editingMenuItem._id, formData);
      } else {
        await supportAPI.addMenuItem(selectedRestaurant._id, formData);
      }
      setShowMenuForm(false);
      setEditingMenuItem(null);
      setMenuForm({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 });
      fetchMenu();
      setMessage({ type: 'success', text: editingMenuItem ? 'Menu item updated' : 'Menu item added' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save menu item' });
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await supportAPI.toggleMenuAvailability(selectedRestaurant._id, item._id);
      fetchMenu();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle availability' });
    }
  };

  const toggleOutOfStock = async (item) => {
    try {
      await supportAPI.toggleOutOfStock(selectedRestaurant._id, item._id);
      fetchMenu();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle stock status' });
    }
  };

  const deleteMenuItem = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await supportAPI.deleteMenuItem(selectedRestaurant._id, item._id);
      fetchMenu();
      setMessage({ type: 'success', text: 'Menu item deleted' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete' });
    }
  };

  // Coupon functions
  const fetchCoupons = async () => {
    if (!selectedRestaurant) return;
    try {
      setCouponLoading(true);
      const { data } = await supportAPI.getRestaurantCoupons(selectedRestaurant._id);
      setCoupons(data.data?.coupons || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load coupons' });
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => { if (selectedRestaurant && activeTab === 'coupons') fetchCoupons(); }, [selectedRestaurant, activeTab]);

  const handleCouponSubmit = async () => {
    try {
      if (editingCoupon) {
        await supportAPI.updateCoupon(selectedRestaurant._id, editingCoupon._id, couponForm);
      } else {
        await supportAPI.createCoupon(selectedRestaurant._id, couponForm);
      }
      setShowCouponForm(false);
      setEditingCoupon(null);
      setCouponForm({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' });
      fetchCoupons();
      setMessage({ type: 'success', text: editingCoupon ? 'Coupon updated' : 'Coupon created' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Failed to save coupon' });
    }
  };

  const toggleCoupon = async (coupon) => {
    try {
      await supportAPI.toggleCoupon(selectedRestaurant._id, coupon._id);
      fetchCoupons();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to toggle coupon' });
    }
  };

  const deleteCoupon = async (coupon) => {
    if (!window.confirm(`Delete coupon "${coupon.code}"?`)) return;
    try {
      await supportAPI.deleteCoupon(selectedRestaurant._id, coupon._id);
      fetchCoupons();
      setMessage({ type: 'success', text: 'Coupon deleted' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete coupon' });
    }
  };

  // Settings functions
  const fetchSettings = async () => {
    if (!selectedRestaurant) return;
    try {
      setSettingsLoading(true);
      const { data } = await supportAPI.getRestaurantSettings(selectedRestaurant._id);
      setSettings(data.data?.restaurant || data.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => { if (selectedRestaurant && activeTab === 'settings') fetchSettings(); }, [selectedRestaurant, activeTab]);

  const saveSettings = async () => {
    try {
      await supportAPI.updateRestaurantSettings(selectedRestaurant._id, settings);
      setMessage({ type: 'success', text: 'Settings saved' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
  };

  const cardStyle = { background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '16px' };
  const btnStyle = (bg, color) => ({ padding: '6px 14px', background: bg, color, border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '500' });
  const tabStyle = (isActive) => ({ padding: '10px 20px', background: isActive ? '#f43f5e' : '#f1f5f9', color: isActive ? '#fff' : '#475569', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '14px' });

  // Restaurant selector
  if (!selectedRestaurant) {
    return (
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px' }}>
        <h2 style={{ marginBottom: '16px' }}>Your Assigned Restaurants</h2>
        {message.text && (
          <div style={{ padding: '12px', marginBottom: '12px', borderRadius: '8px', background: message.type === 'error' ? '#fef2f2' : '#f0fdf4', color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>{message.text}</div>
        )}
        <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search restaurants..." style={{ width: '100%', padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }} />
        {loading ? <p>Loading...</p> : filteredRestaurants.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
            <p style={{ fontSize: '16px', fontWeight: '600', color: '#475569' }}>No restaurants assigned to you</p>
            <p style={{ color: '#94a3b8' }}>Contact admin to assign restaurants</p>
          </div>
        ) : filteredRestaurants.map(r => (
          <div key={r._id} onClick={() => selectRestaurant(r)} style={{ ...cardStyle, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: '600', fontSize: '15px' }}>{r.name}</div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>{r.address?.city || ''} {r.phone ? `| ${r.phone}` : ''}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: r.isOpen ? '#dcfce7' : '#fef2f2', color: r.isOpen ? '#16a34a' : '#dc2626' }}>
                {r.isOpen ? 'Open' : 'Closed'}
              </span>
              <span style={{ color: '#f43f5e', fontWeight: '600', fontSize: '14px' }}>Manage &rarr;</span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <button onClick={() => { setSelectedRestaurant(null); setMessage({ type: '', text: '' }); }} style={btnStyle('#f1f5f9', '#475569')}>&larr; Back</button>
          <h2 style={{ margin: '8px 0 0' }}>{selectedRestaurant.name} <span style={{ fontSize: '14px', color: '#94a3b8', fontWeight: '400' }}>{selectedRestaurant.address?.city}</span></h2>
        </div>
      </div>

      {message.text && (
        <div style={{ padding: '12px', marginBottom: '12px', borderRadius: '8px', background: message.type === 'error' ? '#fef2f2' : '#f0fdf4', color: message.type === 'error' ? '#dc2626' : '#16a34a' }}>{message.text}</div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        {['menu', 'coupons', 'settings'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)} style={tabStyle(activeTab === tab)}>
            {tab === 'menu' ? 'Menu' : tab === 'coupons' ? 'Coupons' : 'Settings'}
          </button>
        ))}
      </div>

      {/* Menu Tab */}
      {activeTab === 'menu' && (
        <div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
            <select value={menuCategory} onChange={e => setMenuCategory(e.target.value)} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px' }}>
              <option value="">All Categories</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <input type="text" value={menuSearch} onChange={e => setMenuSearch(e.target.value)} placeholder="Search menu..." style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', flex: 1, minWidth: '150px' }} />
            <button onClick={() => { setShowMenuForm(!showMenuForm); setEditingMenuItem(null); setMenuForm({ name: '', description: '', category: 'Main Course', price: '', isVegetarian: false, preparationTime: 20 }); }} style={btnStyle('#f43f5e', '#fff')}>{showMenuForm ? 'Cancel' : '+ Add Item'}</button>
          </div>

          {showMenuForm && (
            <div style={cardStyle}>
              <h4 style={{ marginTop: 0 }}>{editingMenuItem ? 'Edit Item' : 'Add Menu Item'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <input type="text" placeholder="Name *" value={menuForm.name} onChange={e => setMenuForm({ ...menuForm, name: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="number" placeholder="Price *" value={menuForm.price} onChange={e => setMenuForm({ ...menuForm, price: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="text" value={menuForm.category} onChange={e => setMenuForm({ ...menuForm, category: e.target.value })} list="stm-menu-cats" placeholder="Pick or type a custom category" maxLength={50} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <datalist id="stm-menu-cats">{CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
                <input type="number" placeholder="Prep time (min)" value={menuForm.preparationTime} onChange={e => setMenuForm({ ...menuForm, preparationTime: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="text" placeholder="Description" value={menuForm.description} onChange={e => setMenuForm({ ...menuForm, description: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', gridColumn: 'span 2' }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                  <input type="checkbox" checked={menuForm.isVegetarian} onChange={e => setMenuForm({ ...menuForm, isVegetarian: e.target.checked })} /> Vegetarian
                </label>
                <input type="file" accept="image/*" ref={menuImageRef} style={{ fontSize: '13px' }} />
              </div>
              <button onClick={handleMenuSubmit} style={{ ...btnStyle('#f43f5e', '#fff'), marginTop: '12px', padding: '8px 20px' }}>{editingMenuItem ? 'Update' : 'Add Item'}</button>
            </div>
          )}

          {menuLoading ? <p>Loading menu...</p> : menuItems.length === 0 ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px' }}>No menu items</p> : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {menuItems.map(item => (
                <div key={item._id} style={{ ...cardStyle, display: 'flex', gap: '14px', alignItems: 'center', opacity: item.isHidden ? 0.5 : 1 }}>
                  {item.image && <img src={item.image} alt="" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontWeight: '600' }}>{item.name}</span>
                      {item.isVegetarian && <span style={{ color: '#16a34a', fontSize: '11px', fontWeight: '600', border: '1px solid #16a34a', padding: '1px 5px', borderRadius: '3px' }}>VEG</span>}
                    </div>
                    <div style={{ fontSize: '13px', color: '#64748b' }}>{item.category} | Rs {item.price} | {item.preparationTime}min</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                    <button onClick={() => toggleAvailability(item)} style={btnStyle(item.isHidden ? '#dcfce7' : '#fef3c7', item.isHidden ? '#16a34a' : '#92400e')}>{item.isHidden ? 'Show' : 'Hide'}</button>
                    <button onClick={() => toggleOutOfStock(item)} style={btnStyle(item.isOutOfStock ? '#dcfce7' : '#fef2f2', item.isOutOfStock ? '#16a34a' : '#dc2626')}>{item.isOutOfStock ? 'In Stock' : 'Out of Stock'}</button>
                    <button onClick={() => { setEditingMenuItem(item); setMenuForm({ name: item.name, description: item.description || '', category: item.category, price: item.price, isVegetarian: item.isVegetarian, preparationTime: item.preparationTime || 20 }); setShowMenuForm(true); }} style={btnStyle('#f1f5f9', '#475569')}>Edit</button>
                    <button onClick={() => deleteMenuItem(item)} style={btnStyle('#fef2f2', '#dc2626')}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Coupons Tab */}
      {activeTab === 'coupons' && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <button onClick={() => { setShowCouponForm(!showCouponForm); setEditingCoupon(null); setCouponForm({ code: '', description: '', discountType: 'percentage', discountValue: '', minOrderValue: '', maxDiscount: '', validFrom: '', validUntil: '', usageLimit: '' }); }} style={btnStyle('#f43f5e', '#fff')}>{showCouponForm ? 'Cancel' : '+ Add Coupon'}</button>
          </div>

          {showCouponForm && (
            <div style={cardStyle}>
              <h4 style={{ marginTop: 0 }}>{editingCoupon ? 'Edit Coupon' : 'Add Coupon'}</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <input type="text" placeholder="Code *" value={couponForm.code} onChange={e => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', textTransform: 'uppercase' }} />
                <select value={couponForm.discountType} onChange={e => setCouponForm({ ...couponForm, discountType: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }}>
                  <option value="percentage">Percentage</option>
                  <option value="fixed">Fixed Amount</option>
                </select>
                <input type="number" placeholder="Discount Value *" value={couponForm.discountValue} onChange={e => setCouponForm({ ...couponForm, discountValue: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="number" placeholder="Min Order Value" value={couponForm.minOrderValue} onChange={e => setCouponForm({ ...couponForm, minOrderValue: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="number" placeholder="Max Discount" value={couponForm.maxDiscount} onChange={e => setCouponForm({ ...couponForm, maxDiscount: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="number" placeholder="Usage Limit" value={couponForm.usageLimit} onChange={e => setCouponForm({ ...couponForm, usageLimit: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="text" placeholder="Description" value={couponForm.description} onChange={e => setCouponForm({ ...couponForm, description: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', gridColumn: 'span 2' }} />
                <input type="date" value={couponForm.validFrom} onChange={e => setCouponForm({ ...couponForm, validFrom: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
                <input type="date" value={couponForm.validUntil} onChange={e => setCouponForm({ ...couponForm, validUntil: e.target.value })} style={{ padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px' }} />
              </div>
              <button onClick={handleCouponSubmit} style={{ ...btnStyle('#f43f5e', '#fff'), marginTop: '12px', padding: '8px 20px' }}>{editingCoupon ? 'Update' : 'Create Coupon'}</button>
            </div>
          )}

          {couponLoading ? <p>Loading coupons...</p> : coupons.length === 0 ? <p style={{ color: '#94a3b8', textAlign: 'center', padding: '24px' }}>No coupons</p> : (
            <div style={{ display: 'grid', gap: '10px' }}>
              {coupons.map(coupon => (
                <div key={coupon._id} style={{ ...cardStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: '600' }}>{coupon.code} <span style={{ fontSize: '12px', fontWeight: '400', color: '#64748b' }}>— {coupon.discountType === 'percentage' ? `${coupon.discountValue}%` : `Rs ${coupon.discountValue}`} off</span></div>
                    <div style={{ fontSize: '13px', color: '#94a3b8' }}>{coupon.description || 'No description'} | Used: {coupon.usedCount || 0}{coupon.usageLimit ? `/${coupon.usageLimit}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button onClick={() => toggleCoupon(coupon)} style={btnStyle(coupon.isActive ? '#fef3c7' : '#dcfce7', coupon.isActive ? '#92400e' : '#16a34a')}>{coupon.isActive ? 'Disable' : 'Enable'}</button>
                    <button onClick={() => { setEditingCoupon(coupon); setCouponForm({ code: coupon.code, description: coupon.description || '', discountType: coupon.discountType, discountValue: coupon.discountValue, minOrderValue: coupon.minOrderValue || '', maxDiscount: coupon.maxDiscount || '', validFrom: coupon.validFrom?.slice(0,10) || '', validUntil: coupon.validUntil?.slice(0,10) || '', usageLimit: coupon.usageLimit || '' }); setShowCouponForm(true); }} style={btnStyle('#f1f5f9', '#475569')}>Edit</button>
                    <button onClick={() => deleteCoupon(coupon)} style={btnStyle('#fef2f2', '#dc2626')}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          {settingsLoading || !settings ? <p>Loading settings...</p> : (
            <div style={cardStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <input type="checkbox" checked={settings.isOpen || false} onChange={e => setSettings({ ...settings, isOpen: e.target.checked })} /> Restaurant Open
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <input type="checkbox" checked={settings.isAcceptingOrders || false} onChange={e => setSettings({ ...settings, isAcceptingOrders: e.target.checked })} /> Accepting Orders
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                  <input type="checkbox" checked={settings.autoAcceptOrders || false} onChange={e => setSettings({ ...settings, autoAcceptOrders: e.target.checked })} /> Auto Accept Orders
                </label>
              </div>

              <h4 style={{ marginTop: '20px' }}>Opening Hours</h4>
              {DAYS.map(day => (
                <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ width: '100px', textTransform: 'capitalize', fontSize: '14px' }}>{day}</span>
                  <label style={{ fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <input type="checkbox" checked={settings.openingHours?.[day]?.isOff || false} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...settings.openingHours?.[day], isOff: e.target.checked } } })} /> Off
                  </label>
                  {!settings.openingHours?.[day]?.isOff && (
                    <>
                      <input type="time" value={settings.openingHours?.[day]?.open || '09:00'} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...settings.openingHours?.[day], open: e.target.value } } })} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                      <span>to</span>
                      <input type="time" value={settings.openingHours?.[day]?.close || '22:00'} onChange={e => setSettings({ ...settings, openingHours: { ...settings.openingHours, [day]: { ...settings.openingHours?.[day], close: e.target.value } } })} style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '6px' }} />
                    </>
                  )}
                </div>
              ))}

              <button onClick={saveSettings} style={{ ...btnStyle('#f43f5e', '#fff'), marginTop: '16px', padding: '10px 24px' }}>Save Settings</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SupportRestaurantManager;
